import { useRequestProposalExcelMutation } from '@anytag/graphql';
import { ButtonDropdown, DownloadSubmenuIcon } from '@anytag/shared-atoms';
import { GTM_IDS } from '@anytag/shared-constants';
import { useDownloadFile, useQueryHelper } from '@anytag/shared-hooks';
import { GoogleAuthDialog, useGoogleAuthDownload } from '@anytag/shared-molecules';
import { DownloadFileType, DropdownOption, MenuTypeLevel } from '@anytag/shared-types';
import { cn, getRestApiUrl } from '@anytag/shared-utils';

export interface ProposalDownloadButtonProps {
  disabled?: boolean;
  className?: string;
  engagementId: number;
  influencerIds?: number[];
}

export const ProposalDownloadButton = ({
  disabled,
  className,
  engagementId,
  influencerIds = []
}: ProposalDownloadButtonProps) => {
  const { t, enqueueSnackbar } = useQueryHelper();
  const { downloading, handleDownloadFile } = useDownloadFile();

  const { callRequestProposalExcel, loading: downloadingExcel } = useRequestProposalExcelMutation();

  const {
    canDownloadGoogleReport,
    dialogProps,
    downloadingPptx,
    downloadingSpreadsheet,
    callRequestProposalPptx,
    handleEngProposalGoogleSlideDownload,
    handleEngProposalSpreadsheetDownload
  } = useGoogleAuthDownload({
    goBackUrl: '/engagement/$id/proposal',
    engagementId,
    influencerIds
  });

  const loading = downloading || downloadingPptx || downloadingExcel || downloadingSpreadsheet;

  const downloadOptions: DropdownOption[] = [
    {
      value: 'Profile Sheet',
      menuType: MenuTypeLevel.MULTI_LEVEL,
      label: t('Profile Sheet'),
      submenu: [
        {
          label: t<string>('PowerPoint'),
          value: DownloadFileType.PPTX,
          gtmId: GTM_IDS.engagement.proposal_pptx_download,
          prefixIcon: <DownloadSubmenuIcon icon="powerPoint" />
        },
        ...(canDownloadGoogleReport
          ? [
              {
                label: t<string>('Google Slide'),
                value: DownloadFileType.GOOGLE_SLIDE,
                gtmId: GTM_IDS.engagement.proposal_google_slide_download,
                prefixIcon: <DownloadSubmenuIcon icon="googleSlide" />
              }
            ]
          : [])
      ]
    },
    {
      value: 'Proposal List',
      menuType: MenuTypeLevel.MULTI_LEVEL,
      label: t('Proposal List'),
      submenu: [
        {
          label: t<string>('CSV'),
          value: DownloadFileType.CSV,
          gtmId: GTM_IDS.engagement.proposal_csv_download,
          prefixIcon: <DownloadSubmenuIcon icon="csv" />
        },
        {
          label: t<string>('Excel'),
          value: DownloadFileType.EXCEL,
          gtmId: GTM_IDS.engagement.proposal_excel_download,
          prefixIcon: <DownloadSubmenuIcon icon="excel" />
        },
        ...(canDownloadGoogleReport
          ? [
              {
                label: t('Spreadsheet'),
                value: DownloadFileType.SPREADSHEET,
                gtmId: GTM_IDS.engagement.proposal_spreadsheet_download,
                prefixIcon: <DownloadSubmenuIcon icon="spreadsheet" />
              }
            ]
          : [])
      ]
    }
  ];

  const handleClickDownloadProposal = (type: DownloadFileType) => async () => {
    const variables = { engagementId, influencerIds: influencerIds.length > 0 ? influencerIds : null };

    try {
      switch (type) {
        case DownloadFileType.EXCEL:
          callRequestProposalExcel({ variables });
          enqueueSnackbar(t('succeededInRequest'), { variant: 'success' });
          break;
        case DownloadFileType.PPTX:
          callRequestProposalPptx({ variables: { ...variables, format: 'PPTX' } });
          enqueueSnackbar(t('succeededInRequest'), { variant: 'success' });
          break;

        // Google Auth required for Slide and Spreadsheet
        case DownloadFileType.GOOGLE_SLIDE:
          handleEngProposalGoogleSlideDownload();
          break;
        case DownloadFileType.SPREADSHEET:
          handleEngProposalSpreadsheetDownload();
          break;
        default:
          break;
      }
    } catch (error) {
      enqueueSnackbar(t('failedToDownload'), { variant: 'error' });
    }
  };

  const handleSelectOption = async (option: DropdownOption) => {
    switch (option.value) {
      case DownloadFileType.CSV:
        handleDownloadFile({
          filenamePrefix: 'proposal',
          params: { ids: influencerIds },
          url: getRestApiUrl(`engagement/${engagementId}/proposal/csv/`)
        });
        break;
      case DownloadFileType.PPTX:
        handleClickDownloadProposal(DownloadFileType.PPTX)();
        break;
      case DownloadFileType.SPREADSHEET:
        handleClickDownloadProposal(DownloadFileType.SPREADSHEET)();
        break;
      case DownloadFileType.EXCEL:
        handleClickDownloadProposal(DownloadFileType.EXCEL)();
        break;
      case DownloadFileType.GOOGLE_SLIDE:
        handleClickDownloadProposal(DownloadFileType.GOOGLE_SLIDE)();
        break;
      default:
        break;
    }
  };

  return (
    <>
      <ButtonDropdown
        variant="outline"
        loading={loading}
        disabled={disabled}
        className={cn(className, 'min-w-33')}
        options={downloadOptions}
        title={t('Button.Download')}
        onSelectOption={handleSelectOption}
        prefixIcon={{ icon: 'download', size: '14px' }}
      />
      <GoogleAuthDialog {...dialogProps} />
    </>
  );
};
